import { HttpClient, HttpContext, HttpErrorResponse, httpResource } from '@angular/common/http';
import { Service, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY, SKIP_SUCCESS_ALERT } from '../../../core/http/http-context-tokens';
import { AuthService } from '../../../core/services/auth/auth.service';
import { slugDeServico } from '../../../shared/utils/slug.util';
import { CategoriaDoFavorito, ServicoFavorito, ServicoFavoritoResponse } from '../models/servicos-favoritos.model';
import { TodosServicosCategoria } from '../models/todos-servicos.model';
import { corDaCategoria } from './identidade-categoria.util';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/MeusFavoritos`;

/** O erro fica local: a aba e a tela de Serviços mostram a própria falha, sem o modal do interceptor. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** Erro local e sem o alerta automático de sucesso: a estrela mudando já é a resposta. */
const SILENCIOSO = () => ERRO_LOCAL().set(SKIP_SUCCESS_ALERT, true);

/**
 * Os serviços favoritos de quem está logado, em `api/sai/cartaservico/MeusFavoritos` — a
 * estrela da tela de Serviços e a aba "Serviços favoritos". O favorito é da CONTA, gravado no
 * banco: acompanha o login em qualquer navegador. Não é o destaque editorial do adm.
 *
 * A lista só é buscada com sessão **e** depois que uma tela a pede (`carregar()`): o serviço é
 * da raiz, e sem o pedido ela sairia para qualquer tela que o injetasse. Ela depende do
 * usuário — cada login a refaz, e o logout a esquece.
 *
 * Favoritar e desfavoritar mudam a lista em memória **só depois do 2xx**, sem reler: a estrela
 * muda na hora da resposta e não pisca.
 */
@Service()
export class FavoritosService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private readonly usuario = toSignal(this.auth.currentUser, {
    initialValue: this.auth.currentUserValue,
  });

  private readonly pedidos = signal(false);

  private readonly listaApi = httpResource<ResponseApi<ServicoFavoritoResponse[]>>(() => {
    // `undefined` é "nada a buscar": sem a guarda a chamada sairia sem token e voltaria 401.
    if (!this.pedidos() || !this.usuario()?.token) return undefined;

    return { url: RECURSO, context: ERRO_LOCAL() };
  });

  /** Há sessão no portal. Sem ela, a estrela pede login e a aba mostra "sessão encerrada". */
  readonly logado = computed(() => !!this.usuario()?.token);

  readonly carregando = computed(() => this.listaApi.isLoading());

  readonly falhou = computed(() => this.listaApi.error() !== undefined);

  /**
   * Os favoritos, do mais recente para o mais antigo, ou `null` sem sessão, antes do pedido, na
   * primeira carga ou em falha. O `error()` vem ANTES do `value()`: em erro o `value()` lança.
   * Durante o `reload()` o valor anterior fica — a lista não pisca.
   */
  readonly favoritos = computed<readonly ServicoFavoritoResponse[] | null>(() => {
    if (this.listaApi.error() !== undefined) return null;

    return this.listaApi.value()?.data ?? null;
  });

  private readonly codigos = computed(
    () => new Set((this.favoritos() ?? []).map((favorito) => favorito.codServicoSrv)),
  );

  ehFavorito(codServicoSrv: number): boolean {
    return this.codigos().has(codServicoSrv);
  }

  /**
   * Pede a lista — a tela de Serviços e a aba chamam ao abrir. Chamada de novo, relê: os
   * contadores de acesso mudam a cada página de serviço aberta.
   */
  carregar(): void {
    if (this.pedidos()) {
      this.listaApi.reload();
    } else {
      this.pedidos.set(true);
    }
  }

  /** Refaz a chamada — o "Tentar novamente". */
  recarregar(): void {
    this.listaApi.reload();
  }

  /** Favorita. Idempotente na API: o que já era favorito volta como está. */
  favoritar(codServicoSrv: number): Observable<ServicoFavoritoResponse> {
    return this.http
      .put<ResponseApi<ServicoFavoritoResponse>>(`${RECURSO}/${codServicoSrv}`, null, {
        context: SILENCIOSO(),
      })
      .pipe(
        map((resposta) => resposta.data),
        tap((favorito) =>
          this.aplicar((lista) => [
            favorito,
            ...lista.filter((item) => item.codServicoSrv !== codServicoSrv),
          ]),
        ),
      );
  }

  /**
   * Desfavorita. O `404` também remove: o favorito já não existia (outra aba, outro
   * navegador), e o estado pedido já vale.
   */
  desfavoritar(codServicoSrv: number): Observable<void> {
    return this.http
      .delete<ResponseApi<ServicoFavoritoResponse>>(`${RECURSO}/${codServicoSrv}`, {
        context: SILENCIOSO(),
      })
      .pipe(
        catchError((erro: unknown) =>
          erro instanceof HttpErrorResponse && erro.status === 404 ? of(null) : throwError(() => erro),
        ),
        map(() => undefined),
        tap(() => this.aplicar((lista) => lista.filter((item) => item.codServicoSrv !== codServicoSrv))),
      );
  }

  /**
   * Muda a lista em memória. Sem lista carregada (primeira leitura no ar ou em falha) não há o
   * que mudar: relê, e o servidor diz o estado — inclusive o que acabou de ser gravado.
   */
  private aplicar(mudanca: (lista: readonly ServicoFavoritoResponse[]) => ServicoFavoritoResponse[]): void {
    const atual = this.listaApi.error() === undefined ? this.listaApi.value() : undefined;

    if (!atual) {
      if (this.pedidos()) this.listaApi.reload();
      return;
    }

    this.listaApi.set({ ...atual, data: mudanca(atual.data ?? []) });
  }
}

/**
 * Os favoritos da API cruzados com o catálogo público, na ordem da API. Função pura: é a ponte
 * entre os números do cidadão e o serviço como o órgão o cadastrou.
 *
 * As categorias de cada serviço ficam na ordem editorial do menu (`categorias` já vem nela), e a
 * cor é a cadastrada no adm, traduzida do hex — nunca sorteada. O ícone já vem traduzido para o
 * Lucide pelo catálogo. Favorito que o catálogo não traz (serviço sem categoria publicável) é
 * descartado sem erro.
 */
export function montarFavoritos(
  respostas: readonly ServicoFavoritoResponse[],
  categorias: readonly TodosServicosCategoria[],
): ServicoFavorito[] {
  const porServico = new Map<
    string,
    { titulo: string; descricao: string; link?: string; categorias: CategoriaDoFavorito[] }
  >();

  for (const categoria of categorias) {
    const daCategoria: CategoriaDoFavorito = {
      id: categoria.id,
      titulo: categoria.titulo,
      icone: categoria.icone,
      cor: corDaCategoria(categoria.cor),
    };

    for (const servico of categoria.servicos) {
      const conhecido = porServico.get(servico.id);

      if (conhecido) {
        conhecido.categorias.push(daCategoria);
      } else {
        porServico.set(servico.id, {
          titulo: servico.titulo,
          descricao: servico.descricao,
          link: servico.link,
          categorias: [daCategoria],
        });
      }
    }
  }

  return respostas.flatMap((resposta) => {
    const servico = porServico.get(String(resposta.codServicoSrv));

    if (!servico) return [];

    return [
      {
        id: String(resposta.codServicoSrv),
        codigo: resposta.codServicoSrv,
        titulo: servico.titulo,
        descricao: servico.descricao,
        link: servico.link ?? `/${slugDeServico(servico.titulo)}`,
        categorias: servico.categorias,
        acessos: resposta.intAcessosAcs,
        favoritadoEm: resposta.datInicioSfv,
      },
    ];
  });
}
